/**
 * Follow-ups — the numbers on the clickable count cards (redesign Phase 4, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-redesign-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim). Money formatting is INJECTED
 * (`money`, rowModel.js formatMoney bound to the site's symbol), as rowModel takes its siblings.
 *
 * Aging and Broken appointments do not use summary set A (Due now + call goals). The export draws
 * a row of count cards instead; clicking one narrows the table to it ("Filtering"), clicking it
 * again lets go. FollowupsFilterCards.vue draws them; this file decides what they say.
 *
 *   agingBucketCards   claims / pre-auths by days since sent: 0–30, 31–60, 61–90, over 90 (the
 *                      export's bucketOf). Claims show the claim fees' total, pre-auths a count.
 *   rowsInAgingBucket  the rows a bucket card narrows the table to — the SAME rule the card counts.
 *   balanceCards       Patient balances: outstanding total, $1,000 or more, credits. Not clickable
 *                      in the export, so not here either.
 *   periodCards        Broken appointments: This week / Last week / This month / Last month. The
 *                      numbers are the PAGE'S OWN metrics (BrokenAppointments.vue computeds), passed
 *                      in — never recounted here, so the cards can never disagree with the old page.
 *   rowsForPatients    the list rows of the patients behind a period card (matched on PatNum).
 *
 * ── DAYS SINCE SENT ────────────────────────────────────────────────────────────────────────────
 * Whole calendar days from the row's DateSent to `today` ('YYYY-MM-DD' in the site's zone, the
 * queue's own `today`), read from the 'YYYY-MM-DD' prefix exactly as rowModel.js reads it for the
 * Sent column — so the "N days" under a row's Sent date and the bucket it lands in always agree.
 * A date in the future reads as 0–30 (the export: days ≤ 30). A row whose DateSent cannot be read
 * is in NO bucket: the four cards can then add up to less than the tab's total, never more.
 */

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})(?:$|[ T])/;

/** The four age buckets, in card order. `max` is the oldest a row in the bucket can be. */
export const AGING_BUCKETS = [
  { key: 'b0', label: '0–30 days', max: 30, dot: 'brand' },
  { key: 'b1', label: '31–60 days', max: 60, dot: 'amber' },
  { key: 'b2', label: '61–90 days', max: 90, dot: 'orange' },
  { key: 'b3', label: 'Over 90 days', max: Infinity, dot: 'red' },
];

/** The four period cards, in card order; `metric` names the page's computed pair. */
export const BROKEN_PERIODS = [
  { key: 'thisWeek', label: 'This week' },
  { key: 'lastWeek', label: 'Last week' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
];

function utcDay(value) {
  if (typeof value !== 'string') return null;
  const m = YMD_RE.exec(value);
  if (!m) return null;
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return Date.UTC(Number(m[1]), mm - 1, dd);
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Whole days from `value` to `today` (positive when `value` is earlier); null if either is unreadable. */
export function daysSince(value, today) {
  const a = utcDay(value);
  const b = utcDay(today);
  if (a === null || b === null) return null;
  return Math.round((b - a) / 86400000);
}

/** The bucket key for an age in days, or null for an unreadable age. */
export function agingBucketOf(days) {
  if (days === null || days === undefined || !Number.isFinite(Number(days))) return null;
  const n = Number(days);
  const b = AGING_BUCKETS.find(x => n <= x.max);
  return b ? b.key : null;
}

/** The rows a bucket card narrows the table to. An unknown / empty key ⇒ every row (no filter). */
export function rowsInAgingBucket(rows, key, today) {
  const list = rows || [];
  if (!key || !AGING_BUCKETS.some(b => b.key === key)) return list;
  return list.filter(r => agingBucketOf(daysSince(r && r.DateSent, today)) === key);
}

/**
 * The four aging cards.
 * @param {Array}  rows   the tab's rows (claims or pre-auths)
 * @param {Object} o      { today, kind: 'claims' | 'preauths', money: n → string }
 * @returns [{ key, label, dot, count, total, value, sub }]
 */
export function agingBucketCards(rows, { today, kind, money } = {}) {
  const fmt = typeof money === 'function' ? money : n => String(n);
  const isClaims = kind === 'claims';
  return AGING_BUCKETS.map((b) => {
    const inBucket = rowsInAgingBucket(rows, b.key, today);
    const total = inBucket.reduce((a, r) => a + (toNumber(r && r.ClaimFee) || 0), 0);
    return {
      key: b.key,
      label: b.label,
      dot: b.dot,
      count: inBucket.length,
      total,
      value: isClaims ? fmt(total) : String(inBucket.length),
      sub: isClaims ? plural(inBucket.length, 'claim', 'claims') : plural(inBucket.length, 'pre-auth', 'pre-auths'),
    };
  });
}

/**
 * Patient balances: the three static cards, from EVERY guarantor row the tab loaded (before the
 * page's own "over $" / billing type / credits filters, which narrow the table, not these).
 * PatientAmount > 0 owes the office; < 0 is a credit (owed back); 0 or unreadable is neither.
 */
export function balanceCards(rows, { money } = {}) {
  const fmt = typeof money === 'function' ? money : n => String(n);
  const amounts = (rows || []).map(r => toNumber(r && r.PatientAmount)).filter(n => n !== null);
  const owing = amounts.filter(n => n > 0);
  const credits = amounts.filter(n => n < 0);
  const sum = list => list.reduce((a, n) => a + n, 0);
  return [
    { key: 'outstanding', label: 'Outstanding balances', count: owing.length, value: fmt(sum(owing)), sub: `Across ${plural(owing.length, 'guarantor', 'guarantors')}` },
    { key: 'over1k', label: '$1,000 or more', count: owing.filter(n => n >= 1000).length, value: String(owing.filter(n => n >= 1000).length), sub: 'Guarantors worth calling first' },
    { key: 'credits', label: 'Credits', count: credits.length, value: String(credits.length), sub: `${fmt(Math.abs(sum(credits)))} owed back to patients` },
  ];
}

/**
 * Broken appointments: the four period cards from the page's metrics.
 * @param {Object} metrics { thisWeek: { count, lost }, lastWeek, thisMonth, lastMonth } — the page's
 *                         brokenAppointments<Period> / brokenAppointmentsTotalValue<Period> (either
 *                         may be undefined before the page has loaded: read as 0)
 * @returns [{ key, label, count, value, subStrong, sub }]
 */
export function periodCards(metrics, { money } = {}) {
  const fmt = typeof money === 'function' ? money : n => String(n);
  const m = metrics || {};
  return BROKEN_PERIODS.map((p) => {
    const src = m[p.key] || {};
    const count = toNumber(src.count) || 0;
    const lost = toNumber(src.lost) || 0;
    return {
      key: p.key,
      label: p.label,
      count,
      value: String(count),
      subStrong: count ? fmt(lost) : '',
      sub: count ? 'net production lost' : 'No broken appointments',
    };
  });
}

/** The rows whose PatNum is one of the `people` rows' PatNum (a period card's patients). */
export function rowsForPatients(rows, people) {
  const wanted = {};
  (people || []).forEach((p) => {
    if (p && p.PatNum !== null && p.PatNum !== undefined && p.PatNum !== '') wanted[String(p.PatNum)] = true;
  });
  return (rows || []).filter(r => r && r.PatNum !== null && r.PatNum !== undefined && wanted[String(r.PatNum)]);
}
